import { forwardRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { Container, StyledFormLabel, StyledFormHelperText } from "./CommonStyles";
import { Switch } from "@kaltura/ds-react-components";
import styled from "@emotion/styled";
// noinspection ES6PreferShortImport
import { FormLabel } from "../form/form-label/FormLabel";

export interface SwitchFieldProps {
    className?: string;
    id: string;
    label: string;
    description?: string;
    defaultValue?: boolean;
    onChange?: (checked: boolean) => void;
    required?: boolean;
    disabled?: boolean;
    fontWeightBold?: boolean;
    /**
     * moves the toggle element to the side of the label + description
     */
    isHorizontal?: boolean;
    /**
     * show label
     */
    showLabel?: boolean;
}

const InlineContainer = styled(Container)(({ theme }) => ({
    marginTop: 0,
    flexDirection: "row",
    columnGap: theme.spacing(1),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        display: "flex", // override original
    },
}));

const StyledFormInlineLabel = styled(FormLabel)(({ theme }) => ({
    height: theme.spacing(2),
    position: "relative",
    bottom: 1,
}));

/**
 * switch field
 */
export const SwitchField = forwardRef<any, SwitchFieldProps>(
    (
        {
            className,
            id,
            label,
            description,
            defaultValue,
            onChange: onChangeProp,
            fontWeightBold,
            required = false,
            disabled = false,
            isHorizontal = false,
            showLabel = true,
        },
        ref
    ) => {
        const { control } = useFormContext();

        return (
            <Container className={className} isHorizontal={isHorizontal}>
                <div>
                    <StyledFormLabel htmlFor={id} text={label} required={required} fontWeightBold={fontWeightBold} />
                    <StyledFormHelperText id={`${id}-description`}>{description}</StyledFormHelperText>
                </div>
                <InlineContainer>
                    <Controller
                        control={control}
                        name={id}
                        defaultValue={defaultValue}
                        rules={{ required: required }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <>
                                <Switch
                                    id={id}
                                    inputRef={ref}
                                    onChange={(event, checked) => {
                                        onChange(event, checked);
                                        onChangeProp?.(checked);
                                    }}
                                    onBlur={onBlur}
                                    checked={value}
                                    disabled={disabled}
                                    size={"small"}
                                    inputProps={{ "aria-describedby": `${id}-description` }}
                                />
                                {showLabel && (
                                    <StyledFormInlineLabel
                                        text={value ? translate("On") : translate("Off")}
                                        required={required}
                                    />
                                )}
                            </>
                        )}
                    />
                </InlineContainer>
            </Container>
        );
    }
);
